import { CostCenter, CostCenter2, Employee, ExpenseAccount, PossibleMatchesFilter, VendorTypeEnum } from "@finway-group/shared/lib/models"
import { TransactionStatusEnum } from "@finway-group/shared/lib/models/transaction/transactionStatus.enum"
import Form from "antd/lib/form"
import moment from "moment"
import React, { ChangeEvent, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { FilterFormInterface } from "~Components/filter/filterTypes"
import { FormInputEnum, FormSelectOption } from "~Components/form/form.types"
import { FormMinMax } from "~Components/form/formMinMax"
import { FormMinMaxDate } from "~Components/form/formMinMaxDate"
import { FormMinMaxDateDueDays } from "~Components/form/formMinMaxDateDueDays"
import { FormSelect } from "~Components/form/formSelect"
import { useCompany } from "~Shared/hooks/company.hooks"
import { useIsTravelEnabled } from "~Shared/hooks/featureFlags.hooks"
import { useFilterFormCreditorChange } from "~Shared/hooks/filter.hooks"
import { getTableFilterObjectStore } from "~Shared/hooks/table.hooks"
import { UserService } from "~Shared/services"
import { createDynamicListWithLabel, getDynamicCostCenter2List, getDynamicCostCenterList, getDynamicExpenseAccountList, getExpenseKindList } from "~Shared/utils/filter.utils"
import { CollapseWrapper } from "~Shared/wrapper/collapse.wrapper"
import { VendorSelect } from "../expenseForm/commonInputFields/VendorSelect"
import { createPossibleMatchesFilterRules } from "../rules/possibleMatchesFilter.rules"

const adjustFilterObject = (filterObject: PossibleMatchesFilter): PossibleMatchesFilter => ({
    ...filterObject,
    minDatePurchased: filterObject.minDatePurchased ? moment(filterObject.minDatePurchased).toDate() : undefined,
    maxDatePurchased: filterObject.maxDatePurchased ? moment(filterObject.maxDatePurchased).toDate() : undefined,
    minDueDate: filterObject.minDueDate ? moment(filterObject.minDueDate).toDate() : undefined,
    maxDueDate: filterObject.maxDueDate ? moment(filterObject.maxDueDate).toDate() : undefined,
    minInvoiceDueDate: filterObject.minInvoiceDueDate ? moment(filterObject.minInvoiceDueDate).toDate() : undefined,
    maxInvoiceDueDate: filterObject.maxInvoiceDueDate ? moment(filterObject.maxInvoiceDueDate).toDate() : undefined,
    minAmount: filterObject.minAmount ? Number(filterObject.minAmount) : undefined,
    maxAmount: filterObject.maxAmount ? Number(filterObject.maxAmount) : undefined,
    minProbability: filterObject.minProbability ? Number(filterObject.minProbability) : undefined,
    maxProbability: filterObject.maxProbability ? Number(filterObject.maxProbability) : undefined,
})

export const PossibleMatchFilterForm = ({ table, formInstance, options, onSubmit }: FilterFormInterface) => {
    const { t } = useTranslation()
    const isTravelEnabled = useIsTravelEnabled()
    const tableFilterObject = getTableFilterObjectStore<PossibleMatchesFilter>(table)
    const [possibleMatchesFilter, setPossibleMatchesFilter] = useState(adjustFilterObject(tableFilterObject))
    const { handleFinish, onCreditorChange } = useFilterFormCreditorChange(possibleMatchesFilter, setPossibleMatchesFilter, onSubmit)
    const isVendorFilterSelectActive = !!possibleMatchesFilter?.vendor || !!possibleMatchesFilter?.creditorUser
    const rules = createPossibleMatchesFilterRules(formInstance)
    const employeeList = UserService.getApproverEmployeeList(options.employees as Array<Employee>)
    const expenseKindList = getExpenseKindList(isTravelEnabled)
    const { showCostCentersIds } = useCompany()

    const transactionStatusList: Array<FormSelectOption> = createDynamicListWithLabel("label", [
        { param: "matching.transaction_tag.completed", value: TransactionStatusEnum.COMPLETED },
        { param: "matching.transaction_tag.pending", value: TransactionStatusEnum.PENDING },
        { param: "matching.transaction_tag.failed", value: TransactionStatusEnum.FAILED },
    ])

    useEffect(() => {
        formInstance.resetFields()
        setPossibleMatchesFilter(adjustFilterObject(possibleMatchesFilter))
    }, [])

    useEffect(() => {
        formInstance.validateFields()
    }, [possibleMatchesFilter])

    return (
        <Form id="possibleMatchFilterForm" className="form--filter" form={formInstance} onFinish={handleFinish}>
            <FormMinMax
                header={t("input:filter_form.amount")}
                inputType={FormInputEnum.NUMBER}
                min={{
                    initialValue: possibleMatchesFilter.minAmount,
                    label: t("input:filter_form.min_amount"),
                    name: "minAmount",
                    onChange: (e: ChangeEvent<HTMLInputElement>) => setPossibleMatchesFilter({ ...possibleMatchesFilter, minAmount: +e.target.value }),
                    rules: rules.minAmount,
                    placeholder: t("placeholder:filter_form.min_amount"),
                }}
                max={{
                    initialValue: possibleMatchesFilter.maxAmount,
                    label: t("input:filter_form.max_amount"),
                    name: "maxAmount",
                    onChange: (e: ChangeEvent<HTMLInputElement>) => setPossibleMatchesFilter({ ...possibleMatchesFilter, maxAmount: +e.target.value }),
                    rules: rules.maxAmount,
                    placeholder: t("placeholder:filter_form.max_amount"),
                }}
            />

            <FormMinMaxDate
                header={t("input:filter_form.date_purchased")}
                min={{
                    name: "minDatePurchased",
                    label: t("input:filter_form.from"),
                    placeholder: t("placeholder:filter_form.min_date"),
                    initialValue: possibleMatchesFilter.minDatePurchased,
                    onChange: (v: any) => setPossibleMatchesFilter({ ...possibleMatchesFilter, minDatePurchased: v?.startOf("day") }),
                }}
                max={{
                    name: "maxDatePurchased",
                    label: t("input:filter_form.to"),
                    placeholder: t("placeholder:filter_form.max_date"),
                    initialValue: possibleMatchesFilter.maxDatePurchased,
                    onChange: (v: any) => setPossibleMatchesFilter({ ...possibleMatchesFilter, maxDatePurchased: v?.startOf("day") }),
                }}
                formInstance={formInstance}
            />

            <FormMinMaxDateDueDays
                header={t("input:filter_form.due_date")}
                dueInDaysSelected={possibleMatchesFilter.dueInDaysRadio}
                min={{
                    name: "minInvoiceDueDate",
                    label: t("input:expense_filter.min_date"),
                    placeholder: t("placeholder:expense_filter.min_date"),
                    initialValue: possibleMatchesFilter.minDueDate,
                }}
                max={{
                    name: "maxInvoiceDueDate",
                    label: t("input:expense_filter.max_date"),
                    placeholder: t("placeholder:expense_filter.max_date"),
                    initialValue: possibleMatchesFilter.maxDueDate,
                }}
                days={{
                    name: "dueInDays",
                    label: t("input:filter_form.due_in_days"),
                    placeholder: t("placeholder:filter_form.due_in_days"),
                    initialValue: possibleMatchesFilter.dueInDays,
                    onChange: () => formInstance.validateFields(["dueInDays"]),
                    rules: rules.dueInDays,
                }}
                formInstance={formInstance}
            />

            <FormSelect name="kind" header={t("input:expense_filter.kind")} initialValue={possibleMatchesFilter.kind} options={expenseKindList} isCollapse fallback />

            <CollapseWrapper keyCode="" header={t("input:filter_form.vendor")} isCollapse isActive={isVendorFilterSelectActive}>
                <VendorSelect
                    shouldShowOnlyVerifiedVendors={false}
                    shouldRenderSelectDropdown={true}
                    selectedVendorType={possibleMatchesFilter?.creditorUser ? VendorTypeEnum.USER : VendorTypeEnum.VENDOR}
                    userCreditors={options.userCreditors}
                    selectedCreditorId={possibleMatchesFilter?.vendor ?? possibleMatchesFilter?.creditorUser}
                    formProps={{ name: "vendor", form: formInstance }}
                    onSelect={onCreditorChange}
                />
            </CollapseWrapper>

            <FormSelect
                name="costCenter"
                header={t("input:expense_filter.cost_center")}
                initialValue={possibleMatchesFilter.costCenter}
                options={getDynamicCostCenterList(options.costCenters as Array<CostCenter>, showCostCentersIds)}
                isCollapse
                showSearch
                fallback
            />

            <FormSelect
                name="costCenter2"
                header={t("input:filter_form.cost_center_2")}
                initialValue={possibleMatchesFilter.costCenter2}
                options={getDynamicCostCenter2List(options.costCenters2 as Array<CostCenter2>)}
                isCollapse
                showSearch
                fallback
            />

            <FormSelect
                name="expenseAccount"
                header={t("input:filter_form.expense_account")}
                initialValue={possibleMatchesFilter.expenseAccount}
                options={getDynamicExpenseAccountList(options.expenseAccounts as Array<ExpenseAccount>)}
                isCollapse
                showSearch
                fallback
            />

            <FormSelect
                name="requestedBy"
                header={t("input:expense_filter.requester")}
                initialValue={possibleMatchesFilter.requestedBy}
                options={options.employees ?? []}
                displayImages
                isCollapse
                showSearch
                fallback
            />

            <FormSelect
                name="approvalNeededBy"
                header={t("input:expense_filter.approver")}
                initialValue={possibleMatchesFilter.approvalNeededBy}
                options={employeeList}
                displayImages
                isCollapse
                showSearch
                fallback
            />

            <FormSelect
                name="transactionStatus"
                header={t("input:transaction_filter.transaction_status")}
                initialValue={String(possibleMatchesFilter.transactionStatus ?? "")}
                options={transactionStatusList}
                isCollapse
                fallback
            />

            <FormMinMax
                header={t("input:filter_form.probability_range")}
                inputType={FormInputEnum.NUMBER}
                min={{
                    initialValue: possibleMatchesFilter.minProbability,
                    label: t("input:filter_form.min_probability"),
                    name: "minProbability",
                    onChange: (e: ChangeEvent<HTMLInputElement>) => setPossibleMatchesFilter({ ...possibleMatchesFilter, minProbability: +e.target.value }),
                    rules: rules.minProbability,
                    placeholder: t("placeholder:filter_form.min_probability"),
                }}
                max={{
                    initialValue: possibleMatchesFilter.maxProbability,
                    label: t("input:filter_form.max_probability"),
                    name: "maxProbability",
                    onChange: (e: ChangeEvent<HTMLInputElement>) => setPossibleMatchesFilter({ ...possibleMatchesFilter, maxProbability: +e.target.value }),
                    rules: rules.maxProbability,
                    placeholder: t("placeholder:filter_form.max_probability"),
                }}
            />
        </Form>
    )
}
